import styles from './DeviceIntegration.module.scss';
import {
  RemoveMessage,
  AddIntegrationIcon,
  RemoveIntegrationIcon,
  titleForDeviceIntegrationType,
  supportLinkSlugForDeviceIntegrationType,
} from '../utils';
import React, { useMemo, useEffect, ReactNode, MouseEventHandler, useState } from 'react';
import { CircledIIcon, SyncIcon } from '@components/Icons';
import Integration, {
  DeviceIntegrationProvider,
  FriendType,
  RemoteIntegrationProvider,
} from '@models/Integration';
import { AccountIconLarge, AccountIconSmall, ConnectedAccountIcon } from '../AccountIcon';
import cn from 'classnames';
import {
  promptForUnsupportedConnectedIntegration,
  promptForUnsupportedIntegration,
} from '../UnsupportedIntegration';
import IntegrationIcons from '@components/Icons/IntegrationIcons';
import { pluralize } from '@support/string';
import { useDispatch, useSelector } from 'react-redux';
import {
  DeviceIntegrationStatus,
  selectIntegrationStatusOnDevice,
  selectIntegrations,
} from '@reducers/user/integrations';
import { archiveIntegration, deleteIntegration } from '@actions/integrations';
import { syncDeviceIntegration } from '@actions/deviceIntegrations';
import Tooltip, { Overlay } from '@components/Tooltip';
import { IntegrationActionsMenu, IntegrationStatus } from '../Integration';
import { ClientEnv } from '@app/types';

interface DeviceIntegrationProps {
  isInOnboarding?: boolean;
  statusOnDevice?: DeviceIntegrationStatus;
  className?: string;
}

type ActionOptionProps = DeviceIntegrationProps & {
  accountIcon: ReactNode;
  integrationTitle: ReactNode;
};

type RemoveDeviceIntegrationProps = ActionOptionProps & {
  onSyncClick?: () => void;
  removeIntegration: () => void;
  syncText?: ReactNode;
  canManageFromDesktop?: boolean;
};

type AddDeviceIntegrationProps = ActionOptionProps & {
  addIntegration?: MouseEventHandler;
  directUserWhenUnsupported?: boolean;
  iOSOnly?: boolean;
};

type DeviceIntegrationOptionProps = DeviceIntegrationProps & {
  platform: DeviceIntegrationProvider;
  onUnsupported: () => void;
  openModal: () => void;
  whereSupported?: ClientEnv[];
};

export const ConnectedDeviceIntegration = ({
  removeIntegration,
  integration,
  isInOnboarding,
  accountIcon,
  integrationTitle,
  onSyncClick,
  className,
  manageFrom,
}: RemoveDeviceIntegrationProps & {
  integration?: Integration;
  isInOnboarding?: boolean;
  manageFrom?: ClientEnv[];
}) => {
  const onDeviceStatus = useSelector(
    selectIntegrationStatusOnDevice(integration?.provider as DeviceIntegrationProvider),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  let Wrapper: ({ children }: { children: any }) => JSX.Element = React.Fragment;
  if (isInOnboarding) {
    Wrapper = ConnectedAccountIcon;
  }

  return (
    <div
      className={cn(
        styles.connectedDeviceIntegration,
        className,
        'device-integration-option remove-device-integration-option',
        isInOnboarding && styles.onboarding,
        menuOpen && styles.menuOpen,
      )}
      onClick={isInOnboarding ? removeIntegration : undefined}
    >
      <Wrapper>{accountIcon}</Wrapper>
      {integrationTitle}
      {!isInOnboarding && integration && (
        <IntegrationStatus
          className={styles.integrationStatus}
          integration={integration}
          syncing={onDeviceStatus?.syncing}
          error={onDeviceStatus?.error}
          disconnected={onDeviceStatus?.connected && !onDeviceStatus?.enabled}
          reconnect={onSyncClick}
        />
      )}
      {!isInOnboarding && (
        <IntegrationActionsMenu
          integration={integration}
          syncing={onDeviceStatus?.syncing}
          removeIntegration={removeIntegration}
          syncIntegration={onSyncClick}
          manageFrom={manageFrom}
          toggleMenu={setMenuOpen}
        />
      )}
      {isInOnboarding && <RemoveIntegrationIcon className="integration-option-action-icon" />}
    </div>
  );
};

const AddDeviceIntegration = ({
  statusOnDevice,
  accountIcon,
  integrationTitle,
  addIntegration,
  className,
  isInOnboarding,
  iOSOnly,
}: AddDeviceIntegrationProps) => {
  let ActionIcon = AddIntegrationIcon;

  if (!statusOnDevice?.supported) {
    ActionIcon = CircledIIcon;

    if (isInOnboarding && !iOSOnly) {
      ActionIcon = IntegrationIcons.desktop;
    } else if (isInOnboarding && iOSOnly) {
      ActionIcon = IntegrationIcons.phone;
    }
  }

  const showSync = statusOnDevice?.enabled && statusOnDevice?.syncing;

  return (
    <div
      className={cn(className, 'device-integration-option add-device-integration-option')}
      onClick={addIntegration}
    >
      {
        // use case: when adding an integration on desktop, account won't exist until after it successfully completes once.
        showSync ? (
          <>
            {accountIcon}
            {integrationTitle}
            <span className={cn(styles.sync, 'device-integration-account-info')}>
              <SyncIcon className="animate-spin" />
              <span className={styles.syncText}></span>
              Setting up
            </span>
          </>
        ) : (
          <>
            {accountIcon}
            {integrationTitle}
            <span className="device-integration-account-info" />
          </>
        )
      }
      <ActionIcon className="integration-option-action-icon add-integration" />
    </div>
  );
};

export const IOSOnlyIntegrationOption = ({ platform, className, isInOnboarding }) => {
  const hasIntegration = useSelector(selectIntegrations).hasDeviceIntegration(platform);

  return hasIntegration ? (
    <ConnectedDeviceIntegration
      integrationTitle={titleForDeviceIntegrationType[platform]}
      accountIcon={
        isInOnboarding ? (
          <AccountIconLarge className="integration-option-account-icon" providerOrType={platform} />
        ) : (
          <AccountIconSmall type={platform} />
        )
      }
      className={className}
      removeIntegration={() =>
        promptForUnsupportedConnectedIntegration({
          integrationType: platform,
          forceDarkMode: isInOnboarding,
        })
      }
      canManageFromDesktop={false}
      manageFrom={['iOS']}
      isInOnboarding={isInOnboarding}
    />
  ) : (
    <AddDeviceIntegration
      integrationTitle={titleForDeviceIntegrationType[platform]}
      isInOnboarding={isInOnboarding}
      accountIcon={
        isInOnboarding ? (
          <AccountIconLarge className="integration-option-account-icon" providerOrType={platform} />
        ) : (
          <AccountIconSmall type={platform} />
        )
      }
      addIntegration={promptForUnsupportedIntegration({
        integrationType: 'apple-contacts',
        whereSupported: ['iOS'],
        docsLink: supportLinkSlugForDeviceIntegrationType['apple-contacts'],
        directUser: !isInOnboarding,
        forceDarkMode: isInOnboarding,
      })}
      className={className}
      iOSOnly
    />
  );
};

const friendTypeDisplay = (friendType?: FriendType) => {
  switch (friendType) {
    case 'following':
      return "Importing people you're following";
    case 'followers':
      return 'Importing people who follow you';
    case 'all':
      return 'Importing everyone';
    case 'mutual':
      return 'Importing mutual follows';
  }
};

export default function DeviceIntegrationOption({
  platform,
  onUnsupported,
  openModal,
  className,
  isInOnboarding,
  whereSupported = ['Mac', 'Windows', 'iOS'],
}: DeviceIntegrationOptionProps) {
  const dispatch = useDispatch();
  const userIntegrations = useSelector(selectIntegrations);
  const hasIntegration = userIntegrations.hasDeviceIntegration(platform);
  const integration = userIntegrations.getIntegrationsForProvider(platform)[0];
  const statusOnDevice = userIntegrations.onDeviceStatuses[platform];
  const integrationTitle = integration?.displayName || titleForDeviceIntegrationType[platform];

  const accountIcon = useMemo(() => {
    if (isInOnboarding) {
      return (
        <AccountIconLarge
          className="integration-option-account-icon"
          providerOrType={platform as RemoteIntegrationProvider | DeviceIntegrationProvider}
        />
      );
    } else {
      return <AccountIconSmall className="integration-option-account-icon" provider={platform} />;
    }
  }, [platform]);

  const syncText = useMemo(() => {
    if (!hasIntegration) {
      return null;
    }

    let lastSync = null;
    let connections = 0;

    if (integration) {
      lastSync = integration.lastImport;
      connections = integration.status.total;
    }

    const connectionText =
      platform === 'facebook' ? (
        <>
          &bull; {connections} {pluralize(connections, 'Friend')}
        </>
      ) : (
        <>
          &bull; {connections} {pluralize(connections, 'Connection')}
        </>
      );

    return (
      <>
        {statusOnDevice?.supported && (
          <SyncIcon className={statusOnDevice?.syncing && 'animate-spin'} />
        )}
        {statusOnDevice?.syncing ? (
          <span className={styles.syncText}>Syncing</span>
        ) : lastSync ? (
          <span className={styles.syncText}>
            Synced {lastSync.differenceInSeconds() < 60 ? 'just now' : lastSync.relativeDuration}{' '}
            {connectionText}
            {integration?.metadata?.friend_type && (
              <Tooltip
                placement="top"
                overlay={<Overlay>{friendTypeDisplay(integration.metadata.friend_type)}</Overlay>}
              >
                <CircledIIcon width="10" height="10" />
              </Tooltip>
            )}
          </span>
        ) : (
          <span className={styles.syncText}>There was an issue syncing. Please retry.</span>
        )}
      </>
    );
  }, [integration, statusOnDevice, platform]);

  useEffect(() => {
    if (!['apple-messages', 'apple-phone-log'].includes(platform) || !statusOnDevice?.supported) {
      return;
    }

    if (window.localStorage.getItem('mac_disk_access') === 'true') {
      openModal();
    }
  }, [statusOnDevice?.enabled]);

  function handleAdd() {
    if (!statusOnDevice || !statusOnDevice.supported) {
      onUnsupported();
      return;
    }

    openModal();
  }

  function handleRemove() {
    if (isInOnboarding) {
      window.app.confirm(
        {
          title: `Remove ${titleForDeviceIntegrationType[platform]} Connection`,
          message: `Are you sure you want to remove your ${integrationTitle} connection?`,
          action: 'Remove',
          variant: 'danger',
          forceDarkMode: true,
        },
        async (confirmed) => {
          if (!confirmed) {
            return;
          }

          dispatch(deleteIntegration(integration));
        },
      );
    } else {
      window.app.confirm(
        {
          title: integrationTitle,
          message: <RemoveMessage />,
          actions: [
            { title: 'Delete', variant: 'danger', value: 'delete' },
            { title: 'Keep Copy', variant: 'primary', value: 'archive', autoFocus: true },
          ],
        },
        async (action) => {
          if (action === 'delete') {
            dispatch(deleteIntegration(integration));
          } else if (action === 'archive') {
            dispatch(archiveIntegration(integration));
          }
        },
      );
    }
  }

  function handleSync() {
    if (!statusOnDevice?.supported) {
      onUnsupported();
      return;
    }

    // we might be on a supported platform, but not on a platform with the integration set up
    if (statusOnDevice.supported && !statusOnDevice.enabled) {
      handleAdd();
      return;
    }

    syncDeviceIntegration(platform);
  }

  if (!hasIntegration) {
    return (
      <AddDeviceIntegration
        className={cn(className)}
        integrationTitle={integrationTitle}
        accountIcon={accountIcon}
        addIntegration={handleAdd}
        statusOnDevice={statusOnDevice}
        isInOnboarding={isInOnboarding}
      />
    );
  }

  return (
    <ConnectedDeviceIntegration
      className={cn(className)}
      integration={integration}
      integrationTitle={integrationTitle}
      accountIcon={accountIcon}
      removeIntegration={handleRemove}
      syncText={syncText}
      onSyncClick={handleSync}
      isInOnboarding={isInOnboarding}
      manageFrom={!statusOnDevice?.supported ? whereSupported : undefined}
    />
  );
}
